import {
  SubscriptionCreditBalanceQuery,
  SubscriptionCreditBalanceQueryVariables,
} from "@outschool/gql-frontend-generated";
import { dayjs } from "@outschool/time";
import { gql, useQueryWithPreviousData } from "@outschool/ui-apollo";
import { useSession } from "@outschool/ui-auth";
import React from "react";

import { useTimeZone } from "../providers/TimeZoneProvider";

export const subscriptionCreditBalanceQuery = gql`
  query SubscriptionCreditBalance {
    subscriptionCreditBalance {
      ...SubscriptionCreditBalanceFragment
    }
  }

  fragment SubscriptionCreditBalanceFragment on SubscriptionCreditBalance {
    uid
    status
    currentStatus
    nextBillingCycleStatus
    subscriptionId
    checkoutId
    balance
    openToBuy
    packageName
    numCredits
    creditValue
    availableCredits
    billingCycleStart
    billingCycleEnd
    pausedAt
    willResumeAt
    cancelAtPeriodEnd
    amountCents
    upcomingPackage {
      packageName
      numCredits
      creditValue
      amountCents
    }
    numPaused
    lastPausedDate
    isRefundable
  }
`;

export const useSubscriptionCreditBalance = () => {
  const { isLoggedIn } = useSession();
  const { timeZone } = useTimeZone();

  const { data, loading, refetch } = useQueryWithPreviousData<
    SubscriptionCreditBalanceQuery,
    SubscriptionCreditBalanceQueryVariables
  >(subscriptionCreditBalanceQuery, {
    skip: !isLoggedIn,
  });

  const {
    status: status, // Deprecated. Use `currentStatus` instead.
    currentStatus,
    nextBillingCycleStatus,
    availableCredits,
    subscriptionCreditLimit,
    billingCycleEnd,
    cancelAtPeriodEnd,
    amountCents,
    upcomingPackageCredits,
    upcomingPackageCost,
    pausedAt, // This includes both paused and pause scheduled state for users.
    willResumeAt,
    numPaused,
    lastPausedDate,
    isRefundable,
  } = React.useMemo(() => {
    const creditBalance = data?.subscriptionCreditBalance ?? null;
    const upcomingPackage = creditBalance?.upcomingPackage;
    const subscriptionCreditLimit = creditBalance?.numCredits ?? 0;

    return {
      status: creditBalance?.status, // Deprecated. Use `currentStatus` instead.
      currentStatus: creditBalance?.currentStatus,
      nextBillingCycleStatus: creditBalance?.nextBillingCycleStatus,
      availableCredits: creditBalance?.availableCredits ?? 0,
      subscriptionCreditLimit,
      billingCycleEnd: !!creditBalance?.billingCycleEnd
        ? dayjs.tz(creditBalance?.billingCycleEnd, timeZone).toDate()
        : null,
      cancelAtPeriodEnd: creditBalance?.cancelAtPeriodEnd,
      amountCents: creditBalance?.amountCents ?? 0,
      upcomingPackageCredits: upcomingPackage?.numCredits ?? null,
      upcomingPackageCost: upcomingPackage?.amountCents ?? null,
      pausedAt: creditBalance?.pausedAt ?? null,
      willResumeAt: creditBalance?.willResumeAt ?? null,
      numPaused: creditBalance?.numPaused ?? 0,
      lastPausedDate: creditBalance?.lastPausedDate,
      isRefundable: creditBalance?.isRefundable ?? false,
    };
  }, [data, timeZone]);

  return {
    status,
    currentStatus,
    nextBillingCycleStatus,
    availableCredits,
    subscriptionCreditLimit,
    billingCycleEnd,
    cancelAtPeriodEnd,
    amountCents,
    upcomingPackageCredits,
    upcomingPackageCost,
    pausedAt,
    willResumeAt,
    numPaused,
    lastPausedDate,
    isRefundable,
    loading,
    refetch,
  };
};

export type SubscriptionCreditBalance = ReturnType<
  typeof useSubscriptionCreditBalance
>;
